import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../fetchInterceptor';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  LayoutDashboard,
  FileText,
  Upload,
  MessageCircle,
  ScrollText,
  ClipboardList,
  LogOut,
  HelpCircle,
  ChevronDown,
  Building2,
  ArrowLeftRight,
  KeyRound,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import ChangePortalPasswordDialog from '../../components/portal/ChangePortalPasswordDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const portalNav = [
  { path: '/portal', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { path: '/portal/invoices', label: 'Invoices', icon: FileText },
  { path: '/portal/statement', label: 'Statement', icon: ScrollText },
  { path: '/portal/checklists', label: 'Checklists', icon: ClipboardList },
  { path: '/portal/documents', label: 'Documents', icon: Upload },
  { path: '/portal/messages', label: 'Messages', icon: MessageCircle },
];

const helpTipsFor = (firmName) => [
  { title: 'Dashboard', description: 'Your financial overview at a glance. See outstanding invoices, recent activity, and unread messages.' },
  { title: 'Invoices', description: `View all invoices from ${firmName}. You can pay online or report a payment you've already made.` },
  { title: 'Statement', description: 'A running ledger of all your invoices and payments, with a running balance.' },
  { title: 'Checklists', description: `Upload the documents ${firmName} needs in one place. Drag-and-drop into each slot.` },
  { title: 'Documents', description: `Upload receipts, tax documents, or any files ${firmName} needs. They'll be reviewed promptly.` },
  { title: 'Messages', description: `Communicate directly with ${firmName}. Faster than email, and everything stays in one place.` },
];

export default function ClientPortalLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [portalInfo, setPortalInfo] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/client-portal/info`);
        if (res.ok) {
          const data = await res.json();
          setPortalInfo(data);
        } else if (res.status === 403) {
          navigate('/dashboard');
        }
      } catch (e) {
        console.error('Failed to fetch portal info:', e);
      }
    };
    fetchInfo();
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="client-portal-layout">
      {/* Top Navigation Bar */}
      <header className="h-16 border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-full px-4 lg:px-8">
          {/* Left: Brand + Workspace */}
          <div className="flex items-center gap-4">
            <Link to="/portal" className="flex items-center gap-2">
              <img src="/countbucks-icon-light.png" alt="CountBucks" className="w-7 h-7 block dark:hidden" />
              <img src="/countbucks-icon.png" alt="CountBucks" className="w-7 h-7 hidden dark:block" />
              <span className="font-bold text-lg bg-gradient-to-r from-emerald-400 to-green-300 bg-clip-text text-transparent hidden sm:inline">
                CountBucks
              </span>
            </Link>
            {portalInfo?.workspace_name && (
              <div className="hidden md:flex items-center gap-2 pl-4 border-l border-border">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">{portalInfo.workspace_name}</span>
                <Badge variant="outline" className="text-[10px]">Client Portal</Badge>
              </div>
            )}
          </div>

          {/* Center: Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {portalNav.map(item => {
              const isActive = item.exact
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`portal-nav-${item.label.toLowerCase()}`}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                    isActive
                      ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                  {item.label === 'Messages' && portalInfo?.unread_messages > 0 && (
                    <Badge className="min-w-[18px] h-4 px-1 text-[10px] bg-amber-500 text-white">
                      {portalInfo.unread_messages}
                    </Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right: Help + User */}
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowHelp(!showHelp)}
                    data-testid="portal-help-btn"
                  >
                    <HelpCircle className="w-5 h-5 text-muted-foreground" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>How to use the portal</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="portal-user-menu">
                  <Avatar className="w-7 h-7">
                    <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-500 text-white text-xs">
                      {user?.name?.charAt(0)?.toUpperCase() || 'C'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline text-sm">{portalInfo?.contact?.name || user?.name}</span>
                  <ChevronDown className="w-3 h-3 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-medium text-sm">{portalInfo?.contact?.name || user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {portalInfo?.has_firm_access && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate('/dashboard')}
                      className="cursor-pointer"
                      data-testid="portal-back-to-main-app"
                    >
                      <ArrowLeftRight className="w-4 h-4 mr-2" />
                      Back to main app
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => setShowChangePassword(true)}
                  className="cursor-pointer"
                  data-testid="portal-change-password-btn"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Change portal password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 dark:text-red-400" data-testid="portal-logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden flex items-center gap-1 px-4 py-2 overflow-x-auto border-t border-border bg-card">
          {portalNav.map(item => {
            const isActive = item.exact
              ? location.pathname === item.path
              : location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all",
                  isActive
                    ? "bg-emerald-500/10 text-emerald-600"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                <item.icon className="w-3.5 h-3.5" />
                {item.label}
              </Link>
            );
          })}
        </div>
      </header>

      {/* Help Panel */}
      {showHelp && (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 border-b border-emerald-200 dark:border-emerald-800" data-testid="portal-help-panel">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-300">How to Use Your Client Portal</h3>
              <Button variant="ghost" size="sm" onClick={() => setShowHelp(false)} className="text-emerald-600">
                Dismiss
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {helpTipsFor(portalInfo?.workspace_name || 'them').map((tip, i) => (
                <div key={tip.title} className="bg-white dark:bg-card rounded-lg p-4 border border-emerald-100 dark:border-emerald-800">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 text-xs font-bold">{i + 1}</span>
                    <h4 className="text-sm font-semibold">{tip.title}</h4>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6">
        <Outlet context={{ portalInfo }} />
      </main>

      <ChangePortalPasswordDialog
        open={showChangePassword}
        onOpenChange={setShowChangePassword}
        workspaceName={portalInfo?.workspace_name}
      />
    </div>
  );
}
